import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Loader2, Ticket } from "lucide-react";
import api, { setToken, setStoredUser } from "../lib/api";
import { toast } from "sonner";

export default function RedeemDialog({ open, onClose, onRedeemed }) {
  const [code, setCode] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      const params = new URLSearchParams(window.location.search);
      const c = params.get("invite");
      if (c) setCode(c.toUpperCase());
    }
  }, [open]);

  const reset = () => { setCode(""); setFullName(""); setEmail(""); setPwd(""); };

  const submit = async () => {
    if (!code.trim()) return toast.error("Inserisci il codice invito");
    if (!fullName.trim()) return toast.error("Inserisci il tuo nome");
    if (!email.trim()) return toast.error("Inserisci la tua email");
    if (pwd.length < 8) return toast.error("Password: minimo 8 caratteri");
    setLoading(true);
    try {
      const res = await api.post("/auth/register", {
        invite_code: code.trim().toUpperCase(),
        email: email.trim().toLowerCase(),
        password: pwd,
        full_name: fullName.trim(),
      });
      setToken(res.data.token);
      setStoredUser(res.data.user);
      toast.success(`Benvenuto/a in ${res.data.family.name}!`);
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete("invite");
        window.history.replaceState({}, "", url.toString());
    } catch (err) {
      console.warn("Invite URL cleanup failed", err);
    }
    reset();
    onRedeemed();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Codice non valido");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !loading && onClose()}>
      <DialogContent data-testid="redeem-dialog" className="max-w-md rounded-3xl border-[#E8E1D9] bg-white">
        <DialogHeader>
          <div className="mb-2 inline-flex w-fit items-center gap-2 rounded-full border border-[#E8E1D9] bg-[#FBF9F6] px-3 py-1">
            <Ticket className="h-3 w-3 text-[#FF7C43]" />
            <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#C87941]">Codice invito</span>
          </div>
          <DialogTitle className="font-display text-2xl font-bold text-[#2D2926]">Crea il tuo account</DialogTitle>
          <DialogDescription className="text-[#5C5855]">
            Usa il codice ricevuto e crea la tua email/password personale per entrare in Casa.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.2em] text-[#C87941]">Codice invito *</label>
            <Input
              data-testid="redeem-code-input"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="ZAP-XXXXX"
              className="rounded-xl border-[#E8E1D9] text-center font-mono text-lg tracking-[0.2em]"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.2em] text-[#C87941]">Il tuo nome *</label>
            <Input
              data-testid="redeem-name-input"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Mario Rossi"
              className="rounded-xl border-[#E8E1D9]"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.2em] text-[#C87941]">Email *</label>
            <Input
              data-testid="redeem-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="mario@example.com"
              className="rounded-xl border-[#E8E1D9]"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.2em] text-[#C87941]">Password (min 8) *</label>
            <Input
              data-testid="redeem-pwd-input"
              type="password"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              placeholder="••••••••"
              className="rounded-xl border-[#E8E1D9]"
              onKeyDown={(e) => e.key === "Enter" && submit()}
            />
          </div>
        </div>

        <DialogFooter className="flex flex-row justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={loading} className="rounded-full border-[#E8E1D9]">Annulla</Button>
          <Button data-testid="redeem-submit-button" type="button" onClick={submit} disabled={loading} className="rounded-full bg-[#FF7C43] text-white hover:bg-[#E66A35]">
            {loading && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            Entra in Casa
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
